import { Attachment, canEditAttachment, Organization, User } from '@ee/common/models';
import { Permission } from '@ee/common/enums';

/**
 * Whether this user may fill in a PDF's AcroForm fields and save the result.
 *
 * The premium FLAG says the product is available to this org; the SUBSCRIPTION says it is currently
 * paid for. Both are required — a lapsed subscription must fall back to a read-only preview rather
 * than a broken editor. [efilingSubscribed] is passed IN rather than resolved here: it costs an HTTP
 * round-trip, and a dialog that fired a billing request on every document preview would flicker its
 * own buttons. The case screen already resolves it once and hands it down.
 *
 * `EFILING_SUBMIT` rather than `EFILING_VIEW`: filling a court form is writing to a document that gets
 * filed, so a read-only e-filing viewer must not be handed an editable one.
 *
 * It ALSO requires document edit on this attachment's kind. Saving a filled form changes an existing
 * document, so the server gates it on `case:{upload,generate}-documents:edit` exactly as it gates a
 * rename. Without this check an e-filer lacking that permission got editable fields and a SAVE that
 * 403s — after the 2-90s upload scan, with their typing lost.
 *
 * This is a UX gate. The enforcement is the endpoint's own `@PreAuthorize` plus its org
 * premium-feature and per-kind edit checks — a client that lies gets a 403, not a saved document.
 *
 * Failing this gate hides SAVE outright. There is deliberately no disabled teaser and no add-on
 * pitch: the preview says nothing about a capability the viewer does not have.
 */
export function canFillPdfForms(
  org?: Organization | null,
  user?: User | null,
  attachment?: Attachment | null,
  efilingSubscribed?: boolean
): boolean {
  return !!efilingSubscribed
    && !!org?.premium_features?.['EFILING']
    && !!user?.permissions?.includes(Permission.EFILING_SUBMIT)
    && canEditAttachment(user?.permissions, attachment);
}
